import React from 'react';
import AsyncSelect from 'react-select/async';
import { useTranslation } from "react-i18next";
import { Chip, Box } from '@mui/material';

const ProductSelect = React.forwardRef(({
  value,
  onChange,
  loadOptions,
  quantity,
  setQuantity,
  price,
  setPrice,
  vat,
  setVat,
  vatOptions,
  onAdd,
  disabled,
  placeholder,
  menuPortalTarget,
  styles,
  formatOptionLabel
}, ref) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = React.useState('');
  
  // Check if this is being used in a form context (with quantity/price) or inline (table)
  const isFormContext = quantity !== undefined && setQuantity !== undefined;
  
  const selectComponent = (
    <div style={{ position: 'relative', width: '100%' }}>
      <AsyncSelect
        ref={ref}
        cacheOptions
        loadOptions={loadOptions}
        defaultOptions
        isClearable
        formatOptionLabel={formatOptionLabel}
        onChange={(option) => {
          onChange(option);
          if (option) {
            setInputValue(''); // Clear search text when option is selected
          }
        }}
        onInputChange={(newValue, actionMeta) => {
          // Keep input value when menu closes or input blurs
          if (actionMeta.action !== 'input-blur' && actionMeta.action !== 'menu-close') {
            setInputValue(newValue);
          }
          return newValue;
        }}
        inputValue={inputValue}
        value={value}
        placeholder={placeholder || t("search_product_placeholder")}
        noOptionsMessage={() => t("no_products_found") || "No products found"}
        loadingMessage={() => t("loading") || "Loading..."}
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPortalTarget ? 'fixed' : undefined}
        styles={{
          container: base => ({ ...base, marginBottom: isFormContext ? 8 : 0, width: '100%' }),
          menu: base => ({ ...base, zIndex: 9999 }),
          ...(styles || {})
        }}
      />
      {inputValue && !isFormContext && (
        <button
          onClick={() => setInputValue('')}
          style={{
            position: 'absolute',
            right: '38px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px 8px',
            fontSize: '16px',
            color: '#999',
            zIndex: 10
          }}
          title={t('clear_search') || 'Clear search'}
        >
          ✕
        </button>
      )}
    </div>
  );
  
  // If not in form context (table usage), return just the select
  if (!isFormContext) {
    return selectComponent;
  }
  
  // Form context - return with section wrapper and additional fields
  return (
    <section style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 22, color: '#1976d2', marginBottom: 12, fontWeight: 600 }}>
        {t("product")}
      </h2>
      {selectComponent}
      {value && (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 16 }}>
          <label>
            {t("quantity")}:
            <input
              type="number"
              min={1}
              value={quantity}
              onChange={e => setQuantity(Number(e.target.value))}
              style={{
                width: 60,
                marginLeft: 6,
                padding: '6px 8px',
                borderRadius: 4,
                border: '1px solid #b0b6c7',
                fontSize: 15,
              }}
            />
          </label>
          <label>
            {t("price")}:
            <input
              type="number"
              value={price}
              onChange={e => setPrice(Number(e.target.value))}
              style={{
                width: 80,
                marginLeft: 6,
                padding: '6px 8px',
                borderRadius: 4,
                border: '1px solid #b0b6c7',
                fontSize: 15,
              }}
            />
          </label>
          <button
            type="button"
            disabled={disabled}
            style={{
              marginLeft: 16,
              background: disabled ? '#b0b6c7' : '#1976d2',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '8px 18px',
              fontWeight: 600,
              fontSize: 15,
              cursor: disabled ? 'not-allowed' : 'pointer',
              boxShadow: '0 2px 8px #e3e6f0',
              transition: 'background 0.2s',
            }}
            onClick={onAdd}
          >
            {t("add_to_invoice")}
          </button>
        </div>
      )}
    </section>
  );
});

ProductSelect.displayName = 'ProductSelect';

export default ProductSelect;